import React from 'react';
import { Link } from 'react-router-dom';
import { VERSION } from '../../../utils/version';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Footer component with links and copyright information
 */
const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { user, isAuthenticated } = useAuth();

  // Only show Technical Documentation link to authenticated @videxa.co users
  const isVidexaEmployee = isAuthenticated && user?.email?.toLowerCase().endsWith('@videxa.co');

  return (
    <footer className="bg-gray-100 dark:bg-gray-900 py-12">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1">
            <div className="bg-white inline-block rounded px-1.5 py-0.5 mb-4">
              <img 
                src="/assets/images/Videxa_Full-p-500.png" 
                alt="Videxa" 
                style={{ height: '1.50rem', objectFit: 'contain' }}
              />
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Advanced AI solutions for intelligent automation.
            </p>
            <div className="flex space-x-4">
              {/* Social Media Icons */}
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400">
                <span className="sr-only">Twitter</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400">
                <span className="sr-only">LinkedIn</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400">
                <span className="sr-only">Instagram</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.017 0C8.396 0 7.989.013 6.77.072 5.548.132 4.735.333 4.033.63a5.892 5.892 0 0 0-2.133 1.39A5.892 5.892 0 0 0 .51 4.153c-.297.702-.498 1.515-.558 2.737C-.069 8.11-.056 8.517-.056 12.138c0 3.62-.013 4.027.072 5.246.06 1.222.261 2.035.558 2.737a5.892 5.892 0 0 0 1.39 2.133 5.892 5.892 0 0 0 2.133 1.39c.702.297 1.515.498 2.737.558 1.219.082 1.626.095 5.246.095 3.62 0 4.027-.013 5.246-.072 1.222-.06 2.035-.261 2.737-.558a5.892 5.892 0 0 0 2.133-1.39 5.892 5.892 0 0 0 1.39-2.133c.297-.702.498-1.515.558-2.737.082-1.219.095-1.626.095-5.246 0-3.62-.013-4.027-.072-5.246-.06-1.222-.261-2.035-.558-2.737a5.892 5.892 0 0 0-1.39-2.133A5.892 5.892 0 0 0 16.846.63C16.144.333 15.331.132 14.109.072 12.89.013 12.483 0 8.864 0 5.243 0 4.836.013 3.617.072 2.395.132 1.582.333.88.63a5.892 5.892 0 0 0-2.133 1.39A5.892 5.892 0 0 0-.643 4.153c-.297.702-.498 1.515-.558 2.737C-1.283 8.11-1.27 8.517-1.27 12.138c0 3.62-.013 4.027.072 5.246.06 1.222.261 2.035.558 2.737a5.892 5.892 0 0 0 1.39 2.133 5.892 5.892 0 0 0 2.133 1.39c.702.297 1.515.498 2.737.558 1.219.082 1.626.095 5.246.095zm0-2.16c-3.564 0-4-.013-5.387-.074-1.281-.06-1.977-.268-2.44-.446a4.075 4.075 0 0 1-1.514-.985 4.075 4.075 0 0 1-.985-1.514c-.178-.463-.386-1.159-.446-2.44-.06-1.387-.074-1.823-.074-5.387 0-3.564.013-4 .074-5.387.06-1.281.268-1.977.446-2.44a4.075 4.075 0 0 1 .985-1.514 4.075 4.075 0 0 1 1.514-.985c.463-.178 1.159-.386 2.44-.446 1.387-.06 1.823-.074 5.387-.074 3.564 0 4 .013 5.387.074 1.281.06 1.977.268 2.44.446a4.075 4.075 0 0 1 1.514.985 4.075 4.075 0 0 1 .985 1.514c.178.463.386 1.159.446 2.44.06 1.387.074 1.823.074 5.387 0 3.564-.013 4-.074 5.387-.06 1.281-.268 1.977-.446 2.44a4.075 4.075 0 0 1-.985 1.514 4.075 4.075 0 0 1-1.514.985c-.463.178-1.159.386-2.44.446-1.387.06-1.823.074-5.387.074zm0-5.838a3.403 3.403 0 1 0 0-6.806 3.403 3.403 0 0 0 0 6.806zm0-8.966a5.563 5.563 0 1 1 0 11.126 5.563 5.563 0 0 1 0-11.126zm8.966-2.133a1.3 1.3 0 1 1-2.6 0 1.3 1.3 0 0 1 2.6 0z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>

          {/* Product Links */}
          <div className="col-span-1">
            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Product</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/features" className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400">
                  Features
                </Link>
              </li>
              <li>
                <Link to="/integrations" className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400">
                  Integrations
                </Link>
              </li>

            </ul>
          </div>

          {/* Resources Links */}
          <div className="col-span-1">
            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Resources</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/documentation" className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400">
                  Documentation
                </Link>
              </li>
              {isVidexaEmployee && (
                <li>
                  <Link to="/technical-docs" className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400">
                    Technical Documentation
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Company Links */}
          <div className="col-span-1">
            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Company</h3>
            <ul className="space-y-2">
              <li>
                              <Link to="/actionable-healthcare-insights" className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400">
                Actionable Healthcare Insights
              </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400">
                  Contact
                </Link>
              </li>

            </ul>
          </div>
        </div>

        {/* Bottom section with legal links */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p 
              className="text-gray-500 dark:text-gray-400 text-sm relative group cursor-default"
              title={VERSION}
            >
              &copy; {currentYear} Videxa. All rights reserved.
              {/* Tooltip */}
              <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
                {VERSION}
              </span>
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link to="/privacy" className="text-gray-500 dark:text-gray-400 text-sm hover:text-primary-600 dark:hover:text-primary-400">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-500 dark:text-gray-400 text-sm hover:text-primary-600 dark:hover:text-primary-400">
                Terms of Service
              </Link>
              <Link to="/cookies" className="text-gray-500 dark:text-gray-400 text-sm hover:text-primary-600 dark:hover:text-primary-400">
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 